import Loader from 'components/Loader';
import useAuth from 'hooks/useAuth.ts';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { useMemo } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useGetTimecardByIdQuery } from 'store/api/timecards';
import { TimecardDto } from 'types/timecards';
import { useClassifiers } from 'utils/useClassifiers';
import { useTimecardPrecision } from 'utils/useTimecardPrecision';
import { timecardDtoToForm } from './timecardFormMapping';
import TimecardPageForm from './TimecardPageForm';

const USED_CLASSIFIERS = ['TIMECARD_ACTIVITY_TYPE', 'REMUNERATION_CALC_TYPE', 'TIMECARD_VALUE_FACTORS'];

const ClassicTimecardEditPage = (): JSX.Element => {
    const { id: timecardId } = useParams();
    const { user } = useAuth();
    const userId = user?.id;
    const userName = user?.name;
    const {
        data: timecard,
        isLoading,
        isError,
    } = useGetTimecardByIdQuery(parseInt(timecardId || '0'), {
        skip: timecardId === undefined,
    });
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: USED_CLASSIFIERS,
    });
    const { isTwoDecimalEntryEnabled } = useTimecardPrecision();

    const initialData = useMemo<TimecardDto | undefined>(() => {
        if (isLoadingClassifiers) {
            return undefined;
        }
        if (timecard) {
            return timecardDtoToForm(timecard, {
                classifiers,
                isTwoDecimalEntryEnabled,
                currentUser: userName && userId ? { name: userName, id: userId } : undefined,
            });
        }
        return undefined;
    }, [classifiers, userId, userName, isTwoDecimalEntryEnabled, timecard, isLoadingClassifiers]);

    if (isLoading || isLoadingClassifiers || (initialData === undefined && !isError)) {
        return <Loader />;
    }

    return <TimecardPageForm type="edit" initialData={initialData} timecard={timecard} />;
};

/** The new design edits the timecard on its record page, opening its one editable card. */
const TimecardEditPage = (): JSX.Element => {
    const isNew = useIsNewDesign();
    const { id } = useParams();
    return isNew ? <Navigate to={`/timecards/${id}?edit=timecard`} replace /> : <ClassicTimecardEditPage />;
};

export default TimecardEditPage;
